import React, { useEffect, useMemo, useRef, useState } from 'react'
import './MapWithFilters.css'
import { LOCALES, getT, getStoredLocale, setStoredLocale } from '../locales'

const API_BASE = (
  process.env.REACT_APP_API_BASE ||
  (
    process.env.NODE_ENV === 'production' &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) ||
  'http://localhost:8001'
).replace(/\/$/, '')
const DEBOUNCE_DELAY = 300
const YANDEX_MAPS_API_KEY = process.env.REACT_APP_YANDEX_MAPS_API_KEY || ''
if (!YANDEX_MAPS_API_KEY) {
  console.error(
    '[MapWithFilters] REACT_APP_YANDEX_MAPS_API_KEY is empty. ' +
    'Set YANDEX_MAPS_API_KEY in infra/k8s/secret.yaml and rebuild the frontend image.'
  )
}

/** Язык карты Яндекс: ru_RU, en_US и т.д. uz/oz → ru_RU (узбекский в API нет). */
function localeToYandexLang(locale) {
  const map = { ru: 'ru_RU', en: 'en_US', uz: 'ru_RU', oz: 'ru_RU' }
  return map[locale] || 'ru_RU'
}

/* Города Узбекистана (алфавитный порядок), центр карты и zoom по умолчанию */
const CITIES = [
  { id: 'andijan', name: 'Андижан', lat: 40.7827, lon: 72.3442, zoom: 12 },
  { id: 'bukhara', name: 'Бухара', lat: 39.7756, lon: 64.4222, zoom: 12 },
  { id: 'guliston', name: 'Гулистон', lat: 40.4897, lon: 68.7842, zoom: 12 },
  { id: 'jizzakh', name: 'Джизак', lat: 40.1158, lon: 67.8422, zoom: 12 },
  { id: 'karshi', name: 'Карши', lat: 38.8636, lon: 65.789, zoom: 12 },
  { id: 'navoi', name: 'Наваи', lat: 40.0844, lon: 65.3792, zoom: 12 },
  { id: 'namangan', name: 'Наманган', lat: 40.9983, lon: 71.6726, zoom: 12 },
  { id: 'nukus', name: 'Нукус', lat: 42.4531, lon: 59.6103, zoom: 12 },
  { id: 'samarkand', name: 'Самарканд', lat: 39.6542, lon: 66.9597, zoom: 12 },
  { id: 'tashkent', name: 'Ташкент', lat: 41.311081, lon: 69.240562, zoom: 12 },
  { id: 'termez', name: 'Термез', lat: 37.2242, lon: 67.2783, zoom: 12 },
  { id: 'urgench', name: 'Ургенч', lat: 41.5347, lon: 60.6233, zoom: 12 },
  { id: 'fergana', name: 'Фергана', lat: 40.3864, lon: 71.7864, zoom: 12 }
]
const DEFAULT_CITY_ID = 'tashkent'

const DEAL_OPTIONS = ['Продажа', 'Аренда', 'Аренда долгосрочная', 'Обмен']
const OBJECT_TYPE_OPTIONS = [
  'Квартиры',
  'Дома',
  'Земля',
  'Гаражи / стоянки',
  'Коммерческие помещения',
  'От застройщика'
]

const ROOM_BUTTONS = [1, 2, 3, 4, '5+']
const VIEWED_ADS_STORAGE_KEY = 'realty-map-viewed-ad-ids'
const VIEWED_ADS_LIMIT = 5000

function loadViewedAdIds() {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(VIEWED_ADS_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.map(String))
  } catch (_) {
    return new Set()
  }
}

function saveViewedAdIds(viewedSet) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(VIEWED_ADS_STORAGE_KEY, JSON.stringify(Array.from(viewedSet)))
  } catch (_) {}
}

/* =========================
   АВТОРИЗАЦИЯ ПО ОДНОРАЗОВОЙ ССЫЛКЕ
   Ссылка вида /?login=<token> обменивается на долгоживущую сессию
   (POST /api/auth/login), токен сессии живёт в localStorage.
========================= */
const AUTH_SESSION_STORAGE_KEY = 'realty-map-auth-session'

function loadAuthToken() {
  if (typeof window === 'undefined') return ''
  try {
    return String(localStorage.getItem(AUTH_SESSION_STORAGE_KEY) || '')
  } catch (_) {
    return ''
  }
}

function saveAuthToken(token) {
  if (typeof window === 'undefined') return
  try {
    if (token) localStorage.setItem(AUTH_SESSION_STORAGE_KEY, token)
    else localStorage.removeItem(AUTH_SESSION_STORAGE_KEY)
  } catch (_) {}
}

/* Токен админской сессии дашборда рассылки (/outreach) — тот же ключ localStorage.
   Бэкенд принимает его в X-Admin-Token наравне с X-Debug-Token, так что админ
   получает debug-режим карты без ?debug= в адресе — но только пока включён
   тумблер «Debug» в шапке (см. DEBUG_TOGGLE_STORAGE_KEY). */
const ADMIN_TOKEN_STORAGE_KEY = 'realty-map-admin-token'

function loadAdminToken() {
  if (typeof window === 'undefined') return ''
  try {
    return String(localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || '')
  } catch (_) {
    return ''
  }
}

function clearAdminToken() {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
  } catch (_) {}
}

/* Тумблер «Debug» в шапке (виден только подтверждённому админу): включает и
   выключает debug-режим карты — тот же, что запрашивает ?debug=<секрет> в URL.
   Положение живёт в localStorage до следующего переключения; по умолчанию
   выключен. Пока он выключен, карта не шлёт в /api/ads ни X-Debug-Token, ни
   X-Admin-Token — бэкенд отдаёт обычную выдачу, и админ видит карту как все. */
const DEBUG_TOGGLE_STORAGE_KEY = 'realty-map-debug-enabled'

function loadDebugToggle() {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(DEBUG_TOGGLE_STORAGE_KEY) === '1'
  } catch (_) {
    return false
  }
}

function saveDebugToggle(enabled) {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(DEBUG_TOGGLE_STORAGE_KEY, enabled ? '1' : '0')
  } catch (_) {}
}

/** Читаемый вид нормализованного номера: 998901234567 → +998 90 123 45 67 */
function formatAuthPhone(phone) {
  const digits = String(phone || '').replace(/\D+/g, '')
  if (!digits) return ''
  if (digits.length === 12 && digits.startsWith('998')) {
    return `+998 ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10)}`
  }
  return `+${digits}`
}

export default function MapWithFilters() {
  /* =========================
     MAP REFS
  ========================= */
  const mapInstanceRef = useRef(null)
  const ymapsRef = useRef(null)
  const clustererRef = useRef(null)
  const fetchTimerRef = useRef(null)
  const flashTimerRef = useRef(null)
  const markerLayoutRef = useRef(null)
  const previousMarkerLayoutRef = useRef(null)
  const clusterIconLayoutRef = useRef(null)
  const initialMapStateRef = useRef(null)
  const balloonMaxWidthRef = useRef(300)
  const urlSyncReadyRef = useRef(false)
  const syncUrlRef = useRef(() => {})
  const fetchAndRenderRef = useRef(null)
  // Последний загруженный bbox (вьюпорт + запас). Пока карта внутри него —
  // повторные запросы при перетаскивании не нужны.
  const lastBboxRef = useRef(null)
  const viewportFetchTimerRef = useRef(null)
  const urlSyncTimerRef = useRef(null)

  // Диагностика цепочки дозагрузки: window.__mapTrace.join('\n') в консоли
  // покажет последние 200 событий (boundschange → таймер → запрос → ответ).
  const traceMap = (label) => {
    try {
      const t = (window.__mapTrace = window.__mapTrace || [])
      t.push(new Date().toISOString().slice(11, 23) + ' ' + label)
      if (t.length > 200) t.splice(0, t.length - 200)
    } catch (e) { /* noop */ }
  }
  // Номер последнего отправленного запроса объявлений. При непрерывном
  // перетаскивании запросы летят параллельно и могут прийти не по порядку —
  // применяем только ответ самого свежего, иначе устаревший пустой ответ
  // затирает уже нарисованные маркеры (и карта «застревает» пустой).
  const fetchSeqRef = useRef(0)
  const placemarkByAdIdRef = useRef(new Map())
  const activeAdIdRef = useRef('')
  const pendingOpenAdIdRef = useRef('')
  const viewedAdIdsRef = useRef(loadViewedAdIds())
  const changeTokenRef = useRef('')
  const changePickRef = useRef({ active: false, adId: '', origLat: null, origLon: null })
  const tempPickPlacemarkRef = useRef(null)
  const debugTokenRef = useRef('')
  const debugModeRef = useRef(false)
  const globalChangeRef = useRef(false)
  // Токен сессии авторизованного пользователя (localStorage переживает перезагрузку).
  const authTokenRef = useRef(loadAuthToken())
  // Токен админской сессии (дашборд рассылки) — читаем один раз при монтировании.
  const adminTokenRef = useRef(loadAdminToken())

  /* =========================
     STATE
  ========================= */
  const [opts, setOpts] = useState(null)
  const [selectedCityId, setSelectedCityId] = useState(DEFAULT_CITY_ID)
  const [filtersOverlayOpen, setFiltersOverlayOpen] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)
  const [pickingAdId, setPickingAdId] = useState('')
  const [pendingChange, setPendingChange] = useState(null)
  const [changeSubmitting, setChangeSubmitting] = useState(false)
  const [pendingHide, setPendingHide] = useState(null)
  const [hideSubmitting, setHideSubmitting] = useState(false)
  const [locale, setLocale] = useState(getStoredLocale)
  const [ymapsScriptReady, setYmapsScriptReady] = useState(false)
  // Авторизованный пользователь ({ phone }) и открытость меню под аватаркой.
  const [authUser, setAuthUser] = useState(null)
  const [authMenuOpen, setAuthMenuOpen] = useState(false)
  // Бэкенд подтвердил debug-режим (fc.debug) — по секрету из ?debug= или по
  // админскому токену при включённом тумблере «Debug».
  const [debugConfirmed, setDebugConfirmed] = useState(false)
  // Подтверждённый админ ({ login } из GET /api/admin/me): ему в шапке
  // показываем тумблер «Debug» и ссылку «Рассылка».
  const [adminUser, setAdminUser] = useState(null)
  const t = useMemo(() => getT(locale), [locale])
  // Debug-режим: ?debug=<секрет>. Значение здесь только «запрашивает» режим,
  // фактическое включение подтверждает бэкенд, сверяя секрет (LOCATION_EDIT_SECRET).
  const debugToken = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return String(new URLSearchParams(window.location.search).get('debug') || '').trim()
  }, [])
  // Запрошен ли debug-режим: ?debug= в адресе включает его на этот визит, иначе
  // действует сохранённое положение тумблера «Debug» (по умолчанию выключен).
  const [debugEnabled, setDebugEnabled] = useState(() => Boolean(debugToken) || loadDebugToggle())
  const debugEnabledRef = useRef(debugEnabled)
  useEffect(() => {
    debugTokenRef.current = debugToken
  }, [debugToken])
  useEffect(() => {
    debugEnabledRef.current = debugEnabled
  }, [debugEnabled])
  // Секретный режим изменения локации: ?change=<секрет>. Значение сверяется на бэкенде.
  const changeToken = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return String(new URLSearchParams(window.location.search).get('change') || '').trim()
  }, [])
  const changeMode = changeToken.length > 0
  useEffect(() => {
    changeTokenRef.current = changeToken
  }, [changeToken])

  /* =========================
     CHANGE LOCATION MODE
  ========================= */
  const removeTempPick = () => {
    const map = mapInstanceRef.current
    if (
      tempPickPlacemarkRef.current &&
      map && map.geoObjects && typeof map.geoObjects.remove === 'function'
    ) {
      map.geoObjects.remove(tempPickPlacemarkRef.current)
    }
    tempPickPlacemarkRef.current = null
  }

  // Полный выход из режима выбора точки (кнопка «Отмена» в баннере).
  const exitPickMode = () => {
    changePickRef.current = { active: false, adId: '', origLat: null, origLon: null }
    removeTempPick()
    setPendingChange(null)
    setPickingAdId('')
  }

  // Отмена в диалоге подтверждения → вернуться к выбору точки.
  const backToPicking = () => {
    removeTempPick()
    setPendingChange(null)
    if (changePickRef.current) changePickRef.current.active = true
  }

  // Заголовки правки: change-токен из ?change= (админ / персональная ссылка)
  // и/или токен сессии автора (вход по login-ссылке) — бэкенд принимает любой.
  const buildEditHeaders = () => {
    const headers = { 'Content-Type': 'application/json' }
    if (changeTokenRef.current) headers['X-Change-Token'] = changeTokenRef.current
    if (authTokenRef.current) headers['X-Session-Token'] = authTokenRef.current
    return headers
  }

  // Заголовки debug-режима (/api/ads, regeocode): секрет из ?debug= и/или
  // админский токен. Пока тумблер «Debug» выключен — ничего не шлём: бэкенд
  // отвечает обычной выдачей (fc.debug=false), и карта работает как у всех.
  const buildDebugHeaders = (headers = {}) => {
    if (!debugEnabledRef.current) return headers
    if (debugTokenRef.current) headers['X-Debug-Token'] = debugTokenRef.current
    if (adminTokenRef.current) headers['X-Admin-Token'] = adminTokenRef.current
    return headers
  }

  // Текст ошибки правки. Автору из рассылки (без ?change=) сырой detail бэкенда
  // не показываем: 401/403/410 — истёк вход по ссылке, остальное — общая ошибка.
  const editErrorMessage = (status, payload, fallback) => {
    if (!changeMode) {
      if (status === 401 || status === 403 || status === 410) return t('author_session_expired')
      return `${fallback} (${status})`
    }
    const detail = payload && payload.detail ? String(payload.detail) : ''
    return detail || `${fallback} (${status})`
  }

  const submitChangeLocation = async () => {
    const pc = pendingChange
    if (!pc) return
    setChangeSubmitting(true)
    try {
      const resp = await fetch(
        `${API_BASE}/api/ads/${encodeURIComponent(pc.adId)}/location`,
        {
          method: 'POST',
          headers: buildEditHeaders(),
          body: JSON.stringify({ lat: pc.lat, lon: pc.lon })
        }
      )
      let payload = null
      try {
        payload = await resp.json()
      } catch (_) {
        payload = null
      }
      if (!resp.ok) {
        throw new Error(editErrorMessage(resp.status, payload, t('change_save_error')))
      }

      removeTempPick()
      setPendingChange(null)
      setPickingAdId('')
      changePickRef.current = { active: false, adId: '', origLat: null, origLon: null }
      pendingOpenAdIdRef.current = pc.adId
      activeAdIdRef.current = pc.adId
      const fetchFn = fetchAndRenderRef.current
      if (typeof fetchFn === 'function') {
        await fetchFn()
      }
      pushFlash(changeMode ? t('change_save_done') : t('author_save_done'), false, 4200)
    } catch (err) {
      const message = err && err.message ? String(err.message) : t('change_save_error')
      pushFlash(message, true, 4200)
    } finally {
      setChangeSubmitting(false)
    }
  }

  const submitHide = async () => {
    const ph = pendingHide
    if (!ph) return
    setHideSubmitting(true)
    try {
      const resp = await fetch(
        `${API_BASE}/api/ads/${encodeURIComponent(ph.adId)}/hide`,
        {
          method: 'POST',
          headers: buildEditHeaders(),
          body: JSON.stringify({ hidden: ph.makeHidden })
        }
      )
      let payload = null
      try {
        payload = await resp.json()
      } catch (_) {
        payload = null
      }
      if (!resp.ok) {
        throw new Error(editErrorMessage(resp.status, payload, t('change_hide_error')))
      }

      setPendingHide(null)
      pendingOpenAdIdRef.current = ph.adId
      activeAdIdRef.current = ph.adId
      const fetchFn = fetchAndRenderRef.current
      if (typeof fetchFn === 'function') {
        await fetchFn()
      }
      if (ph.makeHidden) {
        pushFlash(changeMode ? t('change_hide_done') : t('author_hide_done'), false, 4200)
      } else {
        pushFlash(t('change_show_done'))
      }
    } catch (err) {
      const message = err && err.message ? String(err.message) : t('change_hide_error')
      pushFlash(message, true, 4200)
    } finally {
      setHideSubmitting(false)
    }
  }

  const pushFlash = (text, isError = false, timeoutMs = 3200) => {
    const normalized = String(text || '').trim()
    if (!normalized) return
    setFlashMessage({ text: normalized, isError: Boolean(isError) })
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current)
    }
    flashTimerRef.current = setTimeout(() => {
      setFlashMessage(null)
      flashTimerRef.current = null
    }, timeoutMs)
  }

  /* =========================
     AUTH: вход по одноразовой ссылке, восстановление сессии, выход
  ========================= */
  useEffect(() => {
    let cancelled = false

    // Одноразовый токен убираем из адресной строки сразу — до того, как
    // пользователь успеет скопировать/пошарить URL с ним.
    const stripLoginParam = () => {
      try {
        const params = new URLSearchParams(window.location.search)
        if (!params.has('login')) return
        params.delete('login')
        const query = params.toString()
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (query ? `?${query}` : '') + window.location.hash
        )
      } catch (_) { /* noop */ }
    }

    const resetAuth = () => {
      authTokenRef.current = ''
      saveAuthToken('')
      setAuthUser(null)
    }

    const run = async () => {
      const loginToken = String(
        new URLSearchParams(window.location.search).get('login') || ''
      ).trim()

      if (loginToken) {
        stripLoginParam()
        try {
          const resp = await fetch(`${API_BASE}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: loginToken })
          })
          let payload = null
          try { payload = await resp.json() } catch (_) { payload = null }
          if (cancelled) return
          if (resp.ok && payload && payload.session_token) {
            authTokenRef.current = String(payload.session_token)
            saveAuthToken(authTokenRef.current)
            setAuthUser({ phone: String(payload.phone || '') })
            // Подсказка длиннее обычного тоста: после входа автор должен понять,
            // что своё объявление можно подвинуть — и скрыть, если бэкенд это
            // разрешает (AUTHOR_HIDE_ENABLED; старый бэкенд поля не шлёт = можно).
            const authorCanHide = payload.author_hide_enabled !== false
            pushFlash(t(authorCanHide ? 'auth_login_success' : 'auth_login_success_no_hide'), false, 7000)
            // Перерисовать маркеры уже с подсветкой «моих» объявлений.
            const fetchFn = fetchAndRenderRef.current
            if (typeof fetchFn === 'function') fetchFn()
          } else {
            pushFlash(t('auth_login_error'), true, 4200)
          }
        } catch (_) {
          if (!cancelled) pushFlash(t('auth_login_error'), true, 4200)
        }
        return
      }

      // Нет токена в URL — пробуем восстановить сохранённую сессию.
      const stored = authTokenRef.current
      if (!stored) return
      try {
        const resp = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { 'X-Session-Token': stored }
        })
        if (cancelled) return
        if (resp.ok) {
          let payload = null
          try { payload = await resp.json() } catch (_) { payload = null }
          if (payload && payload.phone) setAuthUser({ phone: String(payload.phone) })
        } else if (resp.status === 401) {
          resetAuth()
        }
        // Прочие статусы (5xx, сеть моргнула) — сессию не трогаем.
      } catch (_) { /* noop */ }
    }

    run()
    return () => { cancelled = true }
  }, [])

  /* Проверка админской сессии (токен дашборда рассылки в localStorage) —
     GET /api/admin/me. Подтверждённому админу в шапке показываем тумблер
     «Debug» и ссылку «Рассылка»; 401 (протух/отозван/вход выключен) — токен
     забываем, как это делает сам дашборд, и перестаём слать его в запросах.
     503/сеть — токен оставляем, служебные элементы просто не покажем. */
  useEffect(() => {
    const token = adminTokenRef.current
    if (!token) return undefined
    let cancelled = false
    const run = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/admin/me`, {
          headers: { 'X-Admin-Token': token }
        })
        if (cancelled) return
        if (resp.ok) {
          let payload = null
          try { payload = await resp.json() } catch (_) { payload = null }
          if (cancelled) return
          setAdminUser({ login: String((payload && payload.login) || '') })
        } else if (resp.status === 401) {
          adminTokenRef.current = ''
          clearAdminToken()
        }
      } catch (_) { /* noop */ }
    }
    run()
    return () => { cancelled = true }
  }, [])

  // Тумблер «Debug» (только админ): запоминаем положение; при выключении убираем
  // секрет ?debug= из адреса (он больше не нужен и не должен уйти со скопированной
  // ссылкой) и перезапрашиваем выдачу — карточки, маркеры прежних координат и
  // скрытые объявления перерисуются под режим, который подтвердит бэкенд;
  // открытая карточка переоткроется уже с новым содержимым.
  const handleDebugToggle = (enabled) => {
    const next = Boolean(enabled)
    debugEnabledRef.current = next
    setDebugEnabled(next)
    saveDebugToggle(next)
    if (!next && debugTokenRef.current) {
      debugTokenRef.current = ''
      if (syncUrlRef.current) syncUrlRef.current()
    }
    // Открытую карточку переоткрываем после перерисовки (как после скрытия):
    // removeAll() закрывает её balloon, и balloonclose сбрасывает activeAdIdRef.
    const openAdId = activeAdIdRef.current
    if (openAdId) pendingOpenAdIdRef.current = openAdId
    const fetchFn = fetchAndRenderRef.current
    if (typeof fetchFn === 'function') fetchFn()
    pushFlash(next ? t('debug_toggle_on') : t('debug_toggle_off'))
  }

  const handleLogout = async () => {
    const token = authTokenRef.current
    authTokenRef.current = ''
    saveAuthToken('')
    setAuthUser(null)
    setAuthMenuOpen(false)
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { 'X-Session-Token': token }
        })
      } catch (_) { /* сессию уже забыли локально — этого достаточно */ }
    }
    // Перерисовать маркеры без подсветки «моих».
    const fetchFn = fetchAndRenderRef.current
    if (typeof fetchFn === 'function') fetchFn()
    pushFlash(t('auth_logged_out'))
  }

  // Закрытие меню аватарки по клику вне его.
  useEffect(() => {
    if (!authMenuOpen) return
    const onDocPointerDown = (e) => {
      const target = e.target
      if (!(target instanceof Element) || !target.closest('.map-header-auth')) {
        setAuthMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('touchstart', onDocPointerDown)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('touchstart', onDocPointerDown)
    }
  }, [authMenuOpen])

  const [filters, setFilters] = useState({
    parsed_deal_type: DEAL_OPTIONS[0],
    parsed_object_type: OBJECT_TYPE_OPTIONS[0],

    parsed_housing_type: '',

    parsed_rooms_selected: [],
    parsed_rooms_min: '',

    price_min: '',
    price_max: '',
    commission_max_percent: '100',

    total_area_min: '',
    total_area_max: '',
    living_area_min: '',
    living_area_max: '',
    kitchen_area_min: '',
    kitchen_area_max: '',

    floor_min: '',
    floor_max: '',
    floors_total_min: '',
    floors_total_max: '',

    parsed_building_type: '',
    parsed_layout: '',

    parsed_year_built_min: '',
    parsed_year_built_max: '',

    parsed_bathroom: '',
    parsed_furnished: '',

    parsed_ceiling_height_min: '',
    parsed_ceiling_height_max: '',

    parsed_renovation: '',
    parsed_gas: '',

    parsed_plot_area_min: '',
    parsed_plot_area_max: '',
    parsed_plot_type: '',
    parsed_land_purpose: '',
    parsed_house_location_selected: [],

    parsed_house_type: '',

    parsed_usable_area_min: '',
    parsed_usable_area_max: '',

    parsed_parking_slots_min: '',

    parsed_installment: false,
    parsed_mortgage: false,

    parsed_housing_class: '',
    parsed_blocks_count_min: '',
    parsed_blocks_count_max: '',

    parsed_finishing: '',
    parsed_facade: '',
    parsed_parking_type: '',
    parsed_construction_stage: '',

    parsed_garage_type: '',
    parsed_garage_purpose: '',
    parsed_commercial_location: '',
    parsed_electricity: '',
    parsed_has_parking: '',
    parsed_commercial_has_plot: '',

    features_inside_selected: [],
    features_nearby_selected: [],
    plot_features_selected: [],
    house_conditions_selected: [],
    water_selected: [],
    heating_selected: [],
    communications_selected: [],
    realty_type_selected: []
  })

  const setF = (k, v) => setFilters(prev => ({ ...prev, [k]: v }))

  const mergeOptions = (primary, fallback) => {
    const a = Array.isArray(primary) ? primary : []
    const b = Array.isArray(fallback) ? fallback : []
    const merged = [...b, ...a].filter(Boolean)
    return [...new Set(merged)]
  }

  const normalizeObjectType = value => {
    if (!value) return value
    const v = String(value).trim()
    const map = {
      'От застройшика': 'От застройщика',
      'Коммерческая недвижимость': 'Коммерческие помещения',
      'Коммерция': 'Коммерческие помещения',
      'Гаражи': 'Гаражи / стоянки'
    }
    return map[v] || v
  }

  const dealOptions = useMemo(() => {
    if (!opts) return DEAL_OPTIONS
    const merged = mergeOptions(opts.deal_types, DEAL_OPTIONS)
    return merged.length ? merged : DEAL_OPTIONS
  }, [opts])

  const objectOptions = useMemo(() => {
    if (!opts) return OBJECT_TYPE_OPTIONS
    const raw = mergeOptions(opts.object_types, OBJECT_TYPE_OPTIONS)
    const normalized = raw.map(normalizeObjectType).filter(Boolean)
    const unique = [...new Set(normalized)]
    const ordered = [
      ...OBJECT_TYPE_OPTIONS.filter(v => unique.includes(v)),
      ...unique.filter(v => !OBJECT_TYPE_OPTIONS.includes(v))
    ]
    return ordered.length ? ordered : OBJECT_TYPE_OPTIONS
  }, [opts])

  useEffect(() => {
    if (!objectOptions.length) return
    setFilters(prev => {
      const normalized = normalizeObjectType(prev.parsed_object_type)
      if (normalized && objectOptions.includes(normalized)) {
        if (normalized !== prev.parsed_object_type) {
          return { ...prev, parsed_object_type: normalized }
        }
        return prev
      }
      if (!prev.parsed_object_type || !objectOptions.includes(prev.parsed_object_type)) {
        return { ...prev, parsed_object_type: objectOptions[0] }
      }
      return prev
    })
  }, [objectOptions])

  /* =========================
     INIT FROM URL PARAMS
  ========================= */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const next = {}

    const setIf = key => {
      const value = params.get(key)
      if (value !== null) next[key] = value
    }

    setIf('parsed_deal_type')
    setIf('parsed_object_type')
    setIf('parsed_housing_type')
    setIf('parsed_rooms_min')
    setIf('price_min')
    setIf('price_max')
    setIf('commission_max_percent')
    setIf('total_area_min')
    setIf('total_area_max')
    setIf('living_area_min')
    setIf('living_area_max')
    setIf('kitchen_area_min')
    setIf('kitchen_area_max')
    setIf('floor_min')
    setIf('floor_max')
    setIf('floors_total_min')
    setIf('floors_total_max')
    setIf('parsed_building_type')
    setIf('parsed_layout')
    setIf('parsed_year_built_min')
    setIf('parsed_year_built_max')
    setIf('parsed_bathroom')
    setIf('parsed_furnished')
    setIf('parsed_ceiling_height_min')
    setIf('parsed_ceiling_height_max')
    setIf('parsed_renovation')
    setIf('parsed_gas')
    setIf('parsed_plot_area_min')
    setIf('parsed_plot_area_max')
    setIf('parsed_plot_type')
    setIf('parsed_land_purpose')
    setIf('parsed_house_type')
    setIf('parsed_usable_area_min')
    setIf('parsed_usable_area_max')
    setIf('parsed_parking_slots_min')
    setIf('parsed_housing_class')
    setIf('parsed_blocks_count_min')
    setIf('parsed_blocks_count_max')
    setIf('parsed_finishing')
    setIf('parsed_facade')
    setIf('parsed_parking_type')
    setIf('parsed_construction_stage')
    setIf('parsed_garage_type')
    setIf('parsed_garage_purpose')
    setIf('parsed_commercial_location')
    setIf('parsed_electricity')
    setIf('parsed_has_parking')
    setIf('parsed_commercial_has_plot')

    const toBool = value => value === 'true'

    const installment = params.get('parsed_installment')
    if (installment === 'true' || installment === 'false') {
      next.parsed_installment = toBool(installment)
    }
    const mortgage = params.get('parsed_mortgage')
    if (mortgage === 'true' || mortgage === 'false') {
      next.parsed_mortgage = toBool(mortgage)
    }

    const setArray = (param, key) => {
      const values = params.getAll(param)
      if (values.length) next[key] = values
    }

    setArray('parsed_rooms', 'parsed_rooms_selected')
    setArray('parsed_house_location', 'parsed_house_location_selected')
    setArray('features_inside', 'features_inside_selected')
    setArray('features_nearby', 'features_nearby_selected')
    setArray('plot_features', 'plot_features_selected')
    setArray('house_conditions', 'house_conditions_selected')
    setArray('water', 'water_selected')
    setArray('heating', 'heating_selected')
    setArray('communications', 'communications_selected')
    setArray('realty_type', 'realty_type_selected')

    if (Object.keys(next).length) {
      setFilters(prev => ({ ...prev, ...next }))
    }

    const lat = parseFloat(params.get('lat'))
    const lon = parseFloat(params.get('lon'))
    const zoom = parseInt(params.get('zoom'), 10)
    if (!Number.isNaN(lat) && !Number.isNaN(lon) && !Number.isNaN(zoom)) {
      initialMapStateRef.current = { lat, lon, zoom }
    }

    const adId = params.get('ad_id')
    if (adId) {
      pendingOpenAdIdRef.current = String(adId)
    }

    urlSyncReadyRef.current = true
  }, [])

  /* =========================
     LOAD FILTER OPTIONS
  ========================= */
  useEffect(() => {
    let cancelled = false

    fetch(`${API_BASE}/api/filter-options`)
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        if (!cancelled) setOpts(data || {})
      })
      .catch(() => {
        if (!cancelled) setOpts({})
      })

    return () => { cancelled = true }
  }, [])

  /* =========================
     LOAD YANDEX MAPS SCRIPT (lang = выбранный язык сайта)
  ========================= */
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.ymaps) {
      setYmapsScriptReady(true)
      return
    }
    const lang = localeToYandexLang(locale)
    const src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_MAPS_API_KEY}&lang=${lang}`
    const existing = document.querySelector(`script[src*="api-maps.yandex.ru"]`)
    if (existing) {
      existing.addEventListener('load', () => setYmapsScriptReady(true))
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => setYmapsScriptReady(true)
    script.onerror = () => setYmapsScriptReady(false)
    document.body.appendChild(script)
  }, [locale])

  /* =========================
     INIT YANDEX MAP
  ========================= */
  useEffect(() => {
    if (!opts) return
    if (!ymapsScriptReady || !window.ymaps) return
    if (mapInstanceRef.current) return

    ymapsRef.current = window.ymaps

    ymapsRef.current.ready(() => {
      const defaultCity = CITIES.find(c => c.id === DEFAULT_CITY_ID) || CITIES[0]
      const initial = initialMapStateRef.current || {
        lat: defaultCity.lat,
        lon: defaultCity.lon,
        zoom: defaultCity.zoom
      }
      const map = new ymapsRef.current.Map('map', {
        center: [initial.lat, initial.lon],
        zoom: initial.zoom,
        controls: ['zoomControl']
      })
      mapInstanceRef.current = map

      const maxBalloonWidth = Math.min(300, Math.max(220, window.innerWidth - 40))
      balloonMaxWidthRef.current = maxBalloonWidth

      if (!clusterIconLayoutRef.current) {
        clusterIconLayoutRef.current = ymapsRef.current.templateLayoutFactory.createClass(
          '<div class="cluster-circle-outer"><span class="cluster-circle-count">$[properties.geoObjects.length]</span></div>'
        )
      }
      const clusterSize = 34
      const clusterOffset = -Math.floor(clusterSize / 2)
      const clusterer = new ymapsRef.current.Clusterer({
        hasBalloon: false,
        clusterIconColor: '#30a94d',
        clusterIconLayout: clusterIconLayoutRef.current,
        clusterIconContentLayout: null,
        clusterIconShape: {
          type: 'Rectangle',
          coordinates: [[0, 0], [clusterSize, clusterSize]]
        },
        clusterIconOffset: [clusterOffset, clusterOffset],
        clusterDisableClickZoom: true,
        gridSize: 32
      })
      clusterer.createCluster = function (center, geoObjects) {
        const cluster = ymapsRef.current.Clusterer.prototype.createCluster.call(this, center, geoObjects)
        cluster.events.add('click', () => {
          const rawBounds = cluster.getBounds && cluster.getBounds()
          const centerPoint = (
            cluster.geometry &&
            typeof cluster.geometry.getCoordinates === 'function' &&
            cluster.geometry.getCoordinates()
          ) || center
          const currentZoom = map.getZoom()
          const maxZoom = map.options.get('maxZoom') || 19

          if (!rawBounds || rawBounds.length < 2) {
            map.setCenter(centerPoint, Math.min(maxZoom, currentZoom + 2), { checkZoomRange: true })
            return
          }

          const [[minLat, minLon], [maxLat, maxLon]] = rawBounds
          const spanLat = Math.abs(maxLat - minLat)
          const spanLon = Math.abs(maxLon - minLon)

          // Для очень близких точек делаем явный шаг зума в центр кластера.
          if (spanLat < 0.00005 && spanLon < 0.00005) {
            map.setCenter(centerPoint, Math.min(maxZoom, currentZoom + 3), { checkZoomRange: true })
            return
          }

          const minPad = 0.00015
          const padLat = Math.max(spanLat * 0.35, minPad)
          const padLon = Math.max(spanLon * 0.35, minPad)
          const bounds = [[minLat - padLat, minLon - padLon], [maxLat + padLat, maxLon + padLon]]
          map.setBounds(bounds, { checkZoomRange: true })

          // Если зум почти не изменился — форсируем дополнительный шаг.
          setTimeout(() => {
            const afterZoom = map.getZoom()
            if (afterZoom <= currentZoom && afterZoom < maxZoom) {
              map.setCenter(centerPoint, Math.min(maxZoom, afterZoom + 2), { checkZoomRange: true })
            }
          }, 120)
        })
        return cluster
      }
      clustererRef.current = clusterer
      if (map.geoObjects && typeof map.geoObjects.add === 'function') {
        map.geoObjects.add(clustererRef.current)
      }

      if (map.balloon && map.balloon.options) {
        map.balloon.options.set('closeButton', true)
        map.balloon.options.set('maxWidth', maxBalloonWidth)
        map.balloon.options.set('minWidth', maxBalloonWidth)
      }

      map.events.add('boundschange', () => {
        traceMap('boundschange')
        // Дозагрузка объявлений — ПЕРВОЙ, чтобы никакой сбой URL-синка не мог
        // её сломать. Срабатывает, когда вьюпорт вышел за пределы последнего
        // загруженного bbox (перетаскивание/отдаление). Внутри bbox — тишина.
        if (viewportFetchTimerRef.current) clearTimeout(viewportFetchTimerRef.current)
        viewportFetchTimerRef.current = setTimeout(() => {
          viewportFetchTimerRef.current = null
          const inside = isViewportInsideLastBbox()
          traceMap('vp-timer inside=' + inside)
          if (!inside) {
            const fetchFn = fetchAndRenderRef.current
            if (fetchFn) fetchFn()
          }
        }, 350)
        // URL-синк дебаунсим отдельным таймером: браузеры троттлят
        // history.replaceState (~100 вызовов за 30 сек; Safari кидает
        // SecurityError), а при живом перетаскивании boundschange стреляет
        // на каждое микродвижение.
        if (urlSyncTimerRef.current) clearTimeout(urlSyncTimerRef.current)
        urlSyncTimerRef.current = setTimeout(() => {
          urlSyncTimerRef.current = null
          if (syncUrlRef.current) syncUrlRef.current()
        }, 400)
      })

      // Режим изменения локации: клик по карте задаёт новую точку для объявления.
      map.events.add('click', (e) => {
        const pick = changePickRef.current
        if (pick && pick.active) {
          const coords = e.get('coords')
          if (Array.isArray(coords) && coords.length >= 2) {
            const lat = Number(coords[0])
            const lon = Number(coords[1])
            if (
              tempPickPlacemarkRef.current &&
              map.geoObjects && typeof map.geoObjects.remove === 'function'
            ) {
              map.geoObjects.remove(tempPickPlacemarkRef.current)
            }
            const pm = new ymapsRef.current.Placemark([lat, lon], {}, {
              preset: 'islands#redDotIcon',
              draggable: true,
              zIndex: 999
            })
            pm.events.add('dragend', () => {
              const c = pm.geometry && pm.geometry.getCoordinates()
              if (Array.isArray(c) && c.length >= 2) {
                setPendingChange(prev => (
                  prev ? { ...prev, lat: Number(c[0]), lon: Number(c[1]) } : prev
                ))
              }
            })
            map.geoObjects.add(pm)
            tempPickPlacemarkRef.current = pm
            pick.active = false
            setPendingChange({ adId: pick.adId, lat, lon })
          }
          return
        }

        // Закрывать открытую карточку при клике по карте мимо маркеров/карточки.
        const target = e.get('target')
        if (target !== map) return
        if (map.balloon && typeof map.balloon.isOpen === 'function' && map.balloon.isOpen()) {
          map.balloon.close()
        }
        if (activeAdIdRef.current) {
          activeAdIdRef.current = ''
          if (syncUrlRef.current) syncUrlRef.current()
        }
      })

      // Персональная ссылка (?ad_id=): сначала центрируем карту на объявлении
      // точечным запросом — иначе при bbox-загрузке его может не быть во
      // вьюпорте и карточка не откроется.
      const boot = async () => {
        if (pendingOpenAdIdRef.current) {
          await focusAdById(pendingOpenAdIdRef.current)
        }
        fetchAndRender()
      }
      boot()
    })
  }, [opts, ymapsScriptReady])

  async function focusAdById(adId) {
    try {
      const headers = buildDebugHeaders()
      if (changeTokenRef.current) headers['X-Change-Token'] = changeTokenRef.current
      if (authTokenRef.current) headers['X-Session-Token'] = authTokenRef.current
      const resp = await fetch(
        `${API_BASE}/api/ads?ad_id=${encodeURIComponent(adId)}&limit=1`,
        { headers }
      )
      if (!resp.ok) return
      const fc = await resp.json()
      const feature = fc && Array.isArray(fc.features) ? fc.features[0] : null
      const coords = feature && feature.geometry && feature.geometry.coordinates
      if (Array.isArray(coords) && coords.length >= 2 && mapInstanceRef.current) {
        // GeoJSON отдаёт [lon, lat]; Яндекс-карта ждёт [lat, lon]
        mapInstanceRef.current.setCenter([coords[1], coords[0]], 17)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('focusAdById error', e)
    }
  }

  /* =========================
     RELOAD ADS ON FILTER CHANGE (DEBOUNCED)
  ========================= */
  useEffect(() => {
    if (!opts) return
    if (!mapInstanceRef.current || !clustererRef.current) return

    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current)
    }
    fetchTimerRef.current = setTimeout(() => {
      fetchAndRender()
    }, DEBOUNCE_DELAY)

    return () => {
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current)
      }
    }
  }, [filters, opts])

  /* На больших экранах закрывать оверлей фильтров при ресайзе */
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 901px)')
    const handle = () => {
      if (mql.matches) setFiltersOverlayOpen(false)
    }
    mql.addEventListener('change', handle)
    handle()
    return () => mql.removeEventListener('change', handle)
  }, [])

  useEffect(() => {
    return () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current)
        flashTimerRef.current = null
      }
    }
  }, [])

  /* =========================
     FILTER VISIBILITY CONFIG
  ========================= */
  const FILTERS_BY_OBJECT = {
    'Квартиры': [
      'housing_type',
      'rooms',
      'area_total',
      'area_living',
      'area_kitchen',
      'floor',
      'floors_total',
      'building_type',
      'layout',
      'year_built',
      'bathroom',
      'furnished',
      'ceiling',
      'features_inside',
      'features_nearby',
      'renovation'
    ],

    'Дома': [
      'rooms',
      'area_total',
      'area_living',
      'house_location',
      'floors_total',
      'ceiling',
      'furnished',
      'plot_area',
      'house_conditions',
      'house_type',
      'building_type',
      'water',
      'electricity',
      'heating',
      'gas',
      'bathroom',
      'year_built',
      'plot_features',
      'features_nearby'
    ],

    'Земля': [
      'plot_area',
      'land_purpose',
      'house_location',
      'plot_type',
      'communications'
    ],

    'Гаражи / стоянки': ['garage_type', 'garage_purpose', 'parking_slots', 'area_total'],

    'Коммерческие помещения': [
      'realty_type',
      'area_total',
      'area_usable',
      'commercial_location',
      'commercial_has_plot',
      'floor',
      'floors_total',
      'ceiling',
      'renovation',
      'features_inside',
      'has_parking'
    ],

    'От застройщика': [
      'installment',
      'mortgage',
      'housing_class',
      'blocks_count',
      'floors_total',
      'floor',
      'house_type',
      'finishing',
      'facade',
      'heating',
      'parking_type',
      'construction_stage',
      'area_total',
      'rooms'
    ]
  }

  const objectTypeKey = normalizeObjectType(filters.parsed_object_type)

  /* =========================
     FETCH & RENDER ADS
  ========================= */
  // Вьюпорт карты + запас в пол-экрана с каждой стороны.
  function computePaddedBounds() {
    const map = mapInstanceRef.current
    if (!map || typeof map.getBounds !== 'function') return null
    const b = map.getBounds()
    if (!Array.isArray(b) || b.length < 2) return null
    const [[latMin, lonMin], [latMax, lonMax]] = b
    const padLat = Math.abs(latMax - latMin) * 0.5
    const padLon = Math.abs(lonMax - lonMin) * 0.5
    return {
      latMin: latMin - padLat,
      lonMin: lonMin - padLon,
      latMax: latMax + padLat,
      lonMax: lonMax + padLon
    }
  }

  function isViewportInsideLastBbox() {
    const last = lastBboxRef.current
    const map = mapInstanceRef.current
    if (!last || !map || typeof map.getBounds !== 'function') return false
    const b = map.getBounds()
    if (!Array.isArray(b) || b.length < 2) return false
    const [[latMin, lonMin], [latMax, lonMax]] = b
    return (
      latMin >= last.latMin && lonMin >= last.lonMin &&
      latMax <= last.latMax && lonMax <= last.lonMax
    )
  }

  async function fetchAndRender() {
    if (!ymapsRef.current || !clustererRef.current || !mapInstanceRef.current) {
      return
    }

    const params = new URLSearchParams()
    const skipKeys = new Set([
      'parsed_rooms_selected',
      'parsed_house_location_selected',
      'features_inside_selected',
      'features_nearby_selected',
      'plot_features_selected',
      'house_conditions_selected',
      'water_selected',
      'heating_selected',
      'communications_selected',
      'realty_type_selected'
    ])

    Object.entries(filters).forEach(([key, value]) => {
      if (skipKeys.has(key)) return
      if (value === '' || value === null || value === undefined) return
      if (typeof value === 'boolean') {
        if (!value) return
        params.append(key, String(value))
        return
      }
      params.append(key, String(value))
    })

    const appendArray = (param, values) => {
      if (!Array.isArray(values)) return
      values.forEach(v => {
        if (v !== '' && v !== null && v !== undefined) {
          params.append(param, String(v))
        }
      })
    }

    appendArray('parsed_rooms', filters.parsed_rooms_selected)
    appendArray('parsed_house_location', filters.parsed_house_location_selected)
    appendArray('features_inside', filters.features_inside_selected)
    appendArray('features_nearby', filters.features_nearby_selected)
    appendArray('plot_features', filters.plot_features_selected)
    appendArray('house_conditions', filters.house_conditions_selected)
    appendArray('water', filters.water_selected)
    appendArray('heating', filters.heating_selected)
    appendArray('communications', filters.communications_selected)
    appendArray('realty_type', filters.realty_type_selected)

    // Город НЕ фильтрует выдачу — грузим объявления видимой области карты (bbox
    // с запасом в пол-экрана с каждой стороны), а выбор города в меню только
    // перемещает карту. Дозагрузка происходит, когда вьюпорт выходит за
    // пределы последнего загруженного bbox (см. boundschange).
    const paddedBounds = computePaddedBounds()
    if (paddedBounds) {
      params.append(
        'bbox',
        `${paddedBounds.lonMin},${paddedBounds.latMin},${paddedBounds.lonMax},${paddedBounds.latMax}`
      )
    }
    params.append('limit', '2000')

    const headers = buildDebugHeaders()
    if (changeTokenRef.current) headers['X-Change-Token'] = changeTokenRef.current
    if (authTokenRef.current) headers['X-Session-Token'] = authTokenRef.current

    const seq = ++fetchSeqRef.current
    traceMap('fetch:start seq=' + seq + (paddedBounds ? '' : ' (no-bbox)'))
    try {
      const resp = await fetch(`${API_BASE}/api/ads?${params.toString()}`, { headers })
      if (seq !== fetchSeqRef.current) {
        traceMap('fetch:stale seq=' + seq)
        return // ушёл более свежий запрос — этот ответ устарел
      }
      if (!resp.ok) {
        traceMap('fetch:http-error ' + resp.status + ' seq=' + seq)
        // eslint-disable-next-line no-console
        console.error('Failed to load ads', resp.status)
        return
      }
      const fc = await resp.json()
      if (seq !== fetchSeqRef.current) {
        traceMap('fetch:stale seq=' + seq)
        return
      }
      // Бэкенд подтверждает debug (секрет или админский токен при включённом
      // тумблере) и глобальный change только при совпадении секрета.
      debugModeRef.current = Boolean(fc && fc.debug)
      setDebugConfirmed(Boolean(fc && fc.debug))
      globalChangeRef.current = Boolean(fc && fc.change)
      renderFeatures(fc)
      lastBboxRef.current = paddedBounds
      traceMap('fetch:applied seq=' + seq + ' features=' + ((fc && fc.features && fc.features.length) || 0))
      // Самолечение: пока ответ летел, карту могли утащить за пределы
      // запрошенной области — сразу дозагружаем актуальный вьюпорт.
      // Закрывает любые пропущенные сценарии независимо от их причины.
      if (!isViewportInsideLastBbox()) {
        traceMap('self-heal:refetch')
        if (viewportFetchTimerRef.current) clearTimeout(viewportFetchTimerRef.current)
        viewportFetchTimerRef.current = setTimeout(() => {
          viewportFetchTimerRef.current = null
          const fetchFn = fetchAndRenderRef.current
          if (fetchFn) fetchFn()
        }, 100)
      }
    } catch (e) {
      traceMap('fetch:exception ' + String(e && e.message).slice(0, 80))
      // eslint-disable-next-line no-console
      console.error('fetchAndRender error', e)
    }
  }
  fetchAndRenderRef.current = fetchAndRender

  const formatPriceLabel = value => {
    if (value === null || value === undefined) return ''
    const text = String(value).trim()
    if (!text) return ''
    const digits = text.replace(/[^\d]+/g, ' ').trim()
    if (!digits) return text
    return `${digits.replace(/\s+/g, ' ')} у.е.`
  }

  const getRoomsLabel = value => {
    if (value === null || value === undefined) return ''
    const n = Number(value)
    if (!Number.isFinite(n)) return ''
    return String(n)
  }

  const escapeHtml = str => {
    if (str == null) return ''
    const s = String(str)
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  const isValidImageUrl = (s) => {
    if (typeof s !== 'string') return false
    const t = s.trim()
    return t.length > 8 && (t.startsWith('http://') || t.startsWith('https://'))
  }

  const getImageUrls = (images) => {
    if (images == null) return []
    let list = images
    if (typeof images === 'string') {
      try {
        list = JSON.parse(images)
      } catch {
        list = images.split(/[;\s]+/).map(s => s.trim()).filter(Boolean)
      }
    } else if (typeof images === 'object' && !Array.isArray(images) && images !== null) {
      list = Object.values(images)
    }
    if (!Array.isArray(list)) return []
    const urls = list.map(item => {
      if (typeof item === 'string') return item.trim() || null
      if (item && typeof item === 'object' && item.url) return String(item.url).trim() || null
      return null
    }).filter(isValidImageUrl)
    const seen = new Set()
    return urls.filter(u => {
      if (seen.has(u)) return false
      seen.add(u)
      return true
    })
  }

  const formatArea = (value) => {
    if (value == null) return null
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    return n % 1 === 0 ? String(n) : n.toFixed(1)
  }

  const formatSotki = (value) => {
    if (value == null) return null
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    const sotki = n / 100
    return sotki % 1 === 0 ? String(sotki) : sotki.toFixed(1)
  }

  const normalizeDebugValue = (value) => {
    if (value === null || value === undefined) return null
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (!trimmed) return null
      try {
        return JSON.parse(trimmed)
      } catch {
        return trimmed
      }
    }
    return value
  }

  const toDebugJson = (value, fallback = '') => {
    const normalized = normalizeDebugValue(value)
    if (normalized === null) return fallback
    if (typeof normalized === 'string') return normalized
    try {
      return JSON.stringify(normalized, null, 2)
    } catch {
      return String(normalized)
    }
  }

  const normalizeGeocodingRequestsForDebug = (value) => {
    const normalized = normalizeDebugValue(value)
    if (!Array.isArray(normalized)) return normalized
    return normalized.map(entry => {
      if (!entry || typeof entry !== 'object') return entry
      const url = entry.url
      if (typeof url !== 'string' || !url.includes('%')) return entry
      try {
        return { ...entry, url: decodeURIComponent(url) }
      } catch {
        return entry
      }
    })
  }

  // Персональная ссылка ?change=<token> для конкретного объявления (label + ссылка).
  // change_token приходит с бэкенда только при валидном debug или глобальном change.
  const buildChangeLinkHtml = (props) => {
    const changeToken = String(props.change_token || '').trim()
    if (!changeToken || typeof window === 'undefined') return ''
    // ad_id — чтобы при открытии ссылки карта сразу открыла карточку этого объявления.
    const adId = String(props.id || '').trim()
    const adIdPart = adId ? `&ad_id=${encodeURIComponent(adId)}` : ''
    const changeLink = `${window.location.origin}${window.location.pathname}?change=${encodeURIComponent(changeToken)}${adIdPart}`
    return `
      <div class="balloon-debug-block">
        <div class="balloon-debug-label">${escapeHtml(t('debug_change_link'))}</div>
        <div class="balloon-debug-description"><a href="${escapeHtml(changeLink)}" target="_blank" rel="noopener noreferrer">${escapeHtml(changeLink)}</a></div>
      </div>
    `
  }

  const buildDebugSection = (props) => {
    if (!debugModeRef.current) return ''

    const adId = String(props.id || '').trim()
    const description = String(props.description || '').trim()
    const geocodedBy = String(props.geocoded_by || '').trim()
    const geocodedText = toDebugJson(props.geocoded, '{}')
    const anomaliesText = toDebugJson(props.landmarks_anomalies, '[]')
    const approvedText = toDebugJson(props.landmarks_approved, '[]')
    const geocodingRequestsText = toDebugJson(
      normalizeGeocodingRequestsForDebug(props.geocoding_requests),
      '[]'
    )
    return `
      <div class="balloon-debug">
        <div class="balloon-debug-title">${escapeHtml(t('debug_section_title'))}</div>
        ${buildChangeLinkHtml(props)}
        ${description ? `
          <div class="balloon-debug-block">
            <div class="balloon-debug-label">${escapeHtml(t('debug_description'))}</div>
            <div class="balloon-debug-description">${escapeHtml(description).replace(/\n/g, '<br/>')}</div>
          </div>
        ` : ''}
        ${geocodedBy ? `
          <div class="balloon-debug-block">
            <div class="balloon-debug-label">${escapeHtml(t('debug_geocoded_by'))}</div>
            <div class="balloon-debug-description">${escapeHtml(geocodedBy)}</div>
          </div>
        ` : ''}
        <div class="balloon-debug-block">
          <div class="balloon-debug-label">${escapeHtml(t('debug_geocoded'))}</div>
          <pre class="balloon-debug-json">${escapeHtml(geocodedText)}</pre>
        </div>
        <div class="balloon-debug-block">
          <div class="balloon-debug-label">${escapeHtml(t('debug_landmarks_anomalies'))}</div>
          <pre class="balloon-debug-json">${escapeHtml(anomaliesText)}</pre>
        </div>
        <div class="balloon-debug-block">
          <div class="balloon-debug-label">${escapeHtml(t('debug_landmarks_approved'))}</div>
          <pre class="balloon-debug-json">${escapeHtml(approvedText)}</pre>
        </div>
        <div class="balloon-debug-block">
          <div class="balloon-debug-label">${escapeHtml(t('debug_geocoding_requests'))}</div>
          <pre class="balloon-debug-json">${escapeHtml(geocodingRequestsText)}</pre>
        </div>
        ${adId ? `
          <div class="balloon-debug-actions">
            <button
              type="button"
              class="balloon-debug-regeocode-btn"
              data-ad-id="${escapeHtml(adId)}"
              onclick="window.__regeocodeAd(this); event.stopPropagation(); event.preventDefault(); return false;"
            >${escapeHtml(t('debug_regeocode_button'))}</button>
            <div class="balloon-debug-regeocode-status"></div>
          </div>
        ` : ''}
      </div>
    `
  }

  const buildChangeSection = (props) => {
    const adId = String(props.id || '').trim()
    if (!adId) return ''
    // Автор объявления (вход по персональной login-ссылке): бэкенд ставит
    // can_edit=true только его объявлениям — кнопки показываем и без ?change=.
    const authorEdit = !changeMode && Boolean(props.is_mine) && Boolean(props.can_edit)
    if (!changeMode && !authorEdit) return ''

    // Кнопка «Изменить локацию»: при глобальном change (can_edit=true у всех),
    // при совпадении per-ad токена или у автора (can_edit=true только у его объявлений).
    const canEdit = Boolean(props.can_edit)
    // Кнопка «Скрыть/Показать» — в глобальном change-режиме и у автора для своего
    // объявления, если бэкенд это разрешил (can_hide, выключатель AUTHOR_HIDE_ENABLED).
    const globalChange = globalChangeRef.current
    const canHide = globalChange || (authorEdit && Boolean(props.can_hide))

    const buttons = []
    if (canEdit) {
      buttons.push(`
        <button
          type="button"
          class="balloon-change-btn"
          data-ad-id="${escapeHtml(adId)}"
          onclick="window.__startChangeLocation(this); event.stopPropagation(); event.preventDefault(); return false;"
        >${escapeHtml(t('change_button'))}</button>
      `)
    }
    if (canHide) {
      const isHidden = Boolean(props.hidden)
      const hideLabel = isHidden ? t('change_show_button') : t('change_hide_button')
      buttons.push(`
        <button
          type="button"
          class="balloon-change-btn balloon-change-hide-btn"
          data-ad-id="${escapeHtml(adId)}"
          data-hidden="${isHidden ? '1' : '0'}"
          onclick="window.__toggleHideAd(this); event.stopPropagation(); event.preventDefault(); return false;"
        >${escapeHtml(hideLabel)}</button>
      `)
    }
    // Персональная ссылка показывается и при глобальном change. В debug-режиме её уже
    // печатает debug-панель — чтобы не дублировать, здесь добавляем только вне debug.
    const linkHtml = (globalChange && !debugModeRef.current) ? buildChangeLinkHtml(props) : ''

    if (!buttons.length && !linkHtml) return ''
    const buttonsHtml = buttons.length ? `<div class="balloon-change">${buttons.join('')}</div>` : ''
    // Подсказка автору не обещает «скрыть», если скрытие выключено.
    const hintHtml = authorEdit
      ? `<div class="balloon-author-hint">${escapeHtml(t(canHide ? 'author_card_hint' : 'author_card_hint_no_hide'))}</div>`
      : ''
    return `${hintHtml}${linkHtml}${buttonsHtml}`
  }

  const buildBalloonInfoLine = (props) => {
    const type = (props.parsed_object_type || '').trim()
    const parts = []
    if (type === 'Квартиры') {
      if (props.parsed_rooms != null) parts.push(`${props.parsed_rooms}-комн.`)
      const area = formatArea(props.parsed_total_area)
      if (area) parts.push(`${area} м²`)
    } else if (type === 'Дома') {
      if (props.parsed_rooms != null) parts.push(`${props.parsed_rooms}-комн.`)
      const area = formatArea(props.parsed_total_area)
      if (area) parts.push(`${area} м²`)
      const sotki = formatSotki(props.parsed_plot_area)
      if (sotki) parts.push(`участок ${sotki} сот.`)
    } else if (type === 'Земля') {
      const sotki = formatSotki(props.parsed_plot_area)
      if (sotki) parts.push(`${sotki} сот.`)
    } else if (type === 'Гаражи / стоянки' || type === 'Гаражи') {
      if (props.parsed_garage_type) parts.push(props.parsed_garage_type)
      if (props.parsed_parking_slots != null) parts.push(`${props.parsed_parking_slots} маш.`)
    } else if (type === 'Коммерческие помещения' || type === 'Коммерческая недвижимость') {
      if (props.parsed_house_type) parts.push(props.parsed_house_type)
      const area = formatArea(props.parsed_usable_area) || formatArea(props.parsed_total_square)
      if (area) parts.push(`${area} м²`)
    } else if (type === 'От застройщика' || type === 'От застройшика') {
      if (props.parsed_housing_class) parts.push(props.parsed_housing_class)
      if (props.parsed_building_type) parts.push(props.parsed_building_type)
      if (props.parsed_construction_stage) parts.push(props.parsed_construction_stage)
    }
    const addr = [props.city, props.district].filter(Boolean).join(', ')
    if (addr) parts.push(addr)
    return parts.join(' · ')
  }

  const buildBalloonBody = (props) => {
    const url = (props.url || '').trim() || '#'
    const title = escapeHtml(props.title || '')
    const price = escapeHtml(props.price || '')
    const rawDescription = (props.description || '').trim()
    const descriptionHtml = escapeHtml(rawDescription).replace(/\n/g, '<br/>')
    const infoLine = escapeHtml(buildBalloonInfoLine(props))
    const debugSection = buildDebugSection(props)
    const changeSection = buildChangeSection(props)
    const images = getImageUrls(props.images)
    const safeId = String(props.id || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64) || 'b'
    const carouselId = `balloon-carousel-${safeId}`

    let carouselHtml = ''
    if (images.length > 0) {
      const slides = images.map((src, i) => {
        const isFirst = i === 0
        const esc = escapeHtml(src)
        const loading = isFirst ? ' loading="eager"' : ' loading="lazy"'
        return `<div class="balloon-slide${isFirst ? ' active' : ''}"><img src="${esc}"${loading} alt="" class="balloon-carousel-img"/></div>`
      }).join('')
      carouselHtml = `
        <div class="balloon-carousel" id="${carouselId}" data-count="${images.length}">
          <div class="balloon-carousel-inner">${slides}</div>
          <button type="button" class="balloon-arrow balloon-arrow-prev" onclick="window.__balloonCarouselPrev('${carouselId}'); event.stopPropagation(); event.preventDefault(); return false;" aria-label="${t('carousel_prev')}">&lt;</button>
          <button type="button" class="balloon-arrow balloon-arrow-next" onclick="window.__balloonCarouselNext('${carouselId}'); event.stopPropagation(); event.preventDefault(); return false;" aria-label="${t('carousel_next')}">&gt;</button>
        </div>`
    } else {
      carouselHtml = `<div class="balloon-carousel balloon-carousel-empty"><div class="balloon-carousel-placeholder">${t('balloon_no_photo')}</div></div>`
    }

    const hiddenBanner = props.hidden
      ? `<div class="balloon-hidden-banner">${escapeHtml(t('balloon_hidden_banner'))}</div>`
      : ''

    return `
      <div class="balloon-card">
        ${hiddenBanner}
        <a class="balloon-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">
          ${carouselHtml}
          <div class="balloon-price">${price}</div>
          ${infoLine ? `<div class="balloon-info">${infoLine}</div>` : ''}
          ${title ? `<div class="balloon-title">${title}</div>` : ''}
          ${rawDescription ? `<div class="balloon-description">${descriptionHtml}</div>` : ''}
        </a>
        ${changeSection}
        ${debugSection}
      </div>`
  }

  const markAdViewed = (adId) => {
    if (adId === undefined || adId === null || adId === '') return
    const id = String(adId)
    if (viewedAdIdsRef.current.has(id)) return

    viewedAdIdsRef.current.add(id)
    if (viewedAdIdsRef.current.size > VIEWED_ADS_LIMIT) {
      const trimmed = Array.from(viewedAdIdsRef.current).slice(-VIEWED_ADS_LIMIT)
      viewedAdIdsRef.current = new Set(trimmed)
    }
    saveViewedAdIds(viewedAdIdsRef.current)
  }

  const openAdCardById = (adId, attempt = 0) => {
    const id = adId == null ? '' : String(adId)
    if (!id) return false

    const map = mapInstanceRef.current
    const clusterer = clustererRef.current
    const placemark = placemarkByAdIdRef.current.get(id)
    if (!map || !clusterer || !placemark) return false

    const state = typeof clusterer.getObjectState === 'function'
      ? clusterer.getObjectState(placemark)
      : null

    if (state && state.isClustered) {
      const coords = placemark.geometry && placemark.geometry.getCoordinates
        ? placemark.geometry.getCoordinates()
        : null
      const currentZoom = map.getZoom()
      const maxZoom = map.options.get('maxZoom') || 19
      if (coords && currentZoom < maxZoom && attempt < 7) {
        map.setCenter(coords, Math.min(maxZoom, currentZoom + 2), { checkZoomRange: true })
        setTimeout(() => openAdCardById(id, attempt + 1), 180)
        return true
      }
    }

    try {
      placemark.balloon.open()
      return true
    } catch (_) {
      return false
    }
  }

  const spreadDuplicateCoordinates = (lat, lon, index, total) => {
    const ringCapacity = 8
    const ring = Math.floor(index / ringCapacity)
    const inRingIndex = index % ringCapacity
    const leftInRing = total - ring * ringCapacity
    const pointsInThisRing = Math.max(1, Math.min(ringCapacity, leftInRing))
    const angle = (2 * Math.PI * inRingIndex) / pointsInThisRing
    const radiusMeters = 6 + ring * 4
    const metersToLat = radiusMeters / 111320
    const cosLat = Math.cos((lat * Math.PI) / 180)
    const metersToLon = radiusMeters / (111320 * Math.max(Math.abs(cosLat), 0.00001))

    const jitterLat = lat + metersToLat * Math.cos(angle)
    const jitterLon = lon + metersToLon * Math.sin(angle)
    return [jitterLat, jitterLon]
  }

  function renderFeatures(fc) {
    if (!clustererRef.current || !ymapsRef.current) return

    clustererRef.current.removeAll()
    placemarkByAdIdRef.current = new Map()

    const features = Array.isArray(fc?.features) ? fc.features : []
    if (!features.length) {
      if (activeAdIdRef.current) {
        activeAdIdRef.current = ''
        if (syncUrlRef.current) syncUrlRef.current()
      }
      return
    }

    const toNumberOrNull = (value) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : null
    }

    const renderItems = []
    features.forEach(f => {
      if (!f || !f.geometry || !Array.isArray(f.geometry.coordinates)) return
      const [lon, lat] = f.geometry.coordinates
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return
      const props = f.properties || {}

      renderItems.push({ kind: 'current', props, lat, lon })
      if (!debugModeRef.current) return

      const previousLat = toNumberOrNull(props.previous_lat)
      const previousLon = toNumberOrNull(props.previous_lon)
      if (previousLat === null || previousLon === null) return
      if (previousLat === lat && previousLon === lon) return
      renderItems.push({ kind: 'previous', props, lat: previousLat, lon: previousLon })
    })

    if (!renderItems.length) {
      if (activeAdIdRef.current) {
        activeAdIdRef.current = ''
        if (syncUrlRef.current) syncUrlRef.current()
      }
      return
    }

    const coordTotals = new Map()
    const coordUsed = new Map()
    const coordKey = (lat, lon) => `${lat.toFixed(6)}|${lon.toFixed(6)}`

    renderItems.forEach(item => {
      const key = coordKey(item.lat, item.lon)
      coordTotals.set(key, (coordTotals.get(key) || 0) + 1)
    })

    if (!markerLayoutRef.current) {
      markerLayoutRef.current = ymapsRef.current.templateLayoutFactory.createClass(
        '<div class="marker-badge$[properties.viewedClass]$[properties.hiddenClass]$[properties.mineClass]"><div class="marker-circle">$[properties.roomsLabel]</div><div class="marker-price">$[properties.priceLabel]</div></div>'
      )
    }
    if (!previousMarkerLayoutRef.current) {
      previousMarkerLayoutRef.current = ymapsRef.current.templateLayoutFactory.createClass(
        '<div class="marker-circle marker-circle-old">$[properties.roomsLabel]</div>'
      )
    }

    const points = renderItems.map(item => {
      const { kind, props, lat, lon } = item
      const key = coordKey(lat, lon)
      const totalAtPoint = coordTotals.get(key) || 1
      const usedAtPoint = coordUsed.get(key) || 0
      coordUsed.set(key, usedAtPoint + 1)
      const [plotLat, plotLon] = totalAtPoint > 1
        ? spreadDuplicateCoordinates(lat, lon, usedAtPoint, totalAtPoint)
        : [lat, lon]

      const adId = props.id == null ? '' : String(props.id)
      if (kind === 'previous') {
        const roomsLabel = getRoomsLabel(props.parsed_rooms) || '•'
        const placemark = new ymapsRef.current.Placemark([plotLat, plotLon], {
          roomsLabel
        }, {
          hasBalloon: false,
          iconLayout: previousMarkerLayoutRef.current,
          iconOffset: [-10, -10],
          iconShape: {
            type: 'Rectangle',
            coordinates: [[0, 0], [19, 19]]
          },
          zIndex: 55
        })
        if (adId) {
          placemark.events.add('click', () => {
            markAdViewed(adId)
            openAdCardById(adId)
          })
        }
        return placemark
      }

      const viewedClass = adId && viewedAdIdsRef.current.has(adId) ? ' marker-badge-viewed' : ''
      const hiddenClass = props.hidden ? ' marker-badge-hidden' : ''
      // Объявление авторизованного пользователя (его телефон упомянут в объявлении).
      const mineClass = props.is_mine ? ' marker-badge-mine' : ''
      const roomsLabel = getRoomsLabel(props.parsed_rooms)
      const priceLabel = formatPriceLabel(props.price)
      const balloonBody = buildBalloonBody(props)
      const hasRooms = roomsLabel !== ''
      const labelLen = priceLabel ? priceLabel.length : 0
      const shapeWidth = Math.max(21, (hasRooms ? 19 : 0) + (labelLen ? labelLen * 7 + (hasRooms ? 12 : 0) : 0))
      const shapeHeight = 21
      const placemark = new ymapsRef.current.Placemark([plotLat, plotLon], {
        balloonContentHeader: '',
        balloonContentBody: balloonBody,
        roomsLabel,
        priceLabel,
        viewedClass,
        hiddenClass,
        mineClass
      }, {
        balloonMaxWidth: balloonMaxWidthRef.current,
        iconLayout: markerLayoutRef.current,
        iconOffset: [hasRooms ? -10 : 0, -10],
        iconShape: {
          type: 'Rectangle',
          coordinates: [[0, 0], [shapeWidth, shapeHeight]]
        }
      })
      if (adId) {
        placemarkByAdIdRef.current.set(adId, placemark)
      }
      const markCurrentPlacemarkViewed = () => {
        if (!adId) return
        markAdViewed(adId)
        placemark.properties.set('viewedClass', ' marker-badge-viewed')
      }
      placemark.events.add('click', markCurrentPlacemarkViewed)
      placemark.events.add('balloonopen', () => {
        markCurrentPlacemarkViewed()
        if (!adId) return
        activeAdIdRef.current = adId
        pendingOpenAdIdRef.current = ''
        if (syncUrlRef.current) syncUrlRef.current()
      })
      placemark.events.add('balloonclose', () => {
        if (!adId) return
        if (activeAdIdRef.current === adId) {
          activeAdIdRef.current = ''
          if (syncUrlRef.current) syncUrlRef.current()
        }
      })
      return placemark
    }).filter(Boolean)

    if (points.length) {
      clustererRef.current.add(points)
    }

    if (activeAdIdRef.current && !placemarkByAdIdRef.current.has(activeAdIdRef.current)) {
      activeAdIdRef.current = ''
      if (syncUrlRef.current) syncUrlRef.current()
    }

    const requestedAdId = pendingOpenAdIdRef.current || activeAdIdRef.current
    if (requestedAdId) {
      const started = openAdCardById(requestedAdId)
      if (started && pendingOpenAdIdRef.current === requestedAdId) {
        pendingOpenAdIdRef.current = ''
      }
    }
  }

  /* =========================
     SYNC FILTERS TO URL
  ========================= */
  useEffect(() => {
    syncUrlRef.current = () => {
      if (!urlSyncReadyRef.current) return

      const params = new URLSearchParams()
      const arrayMappings = [
        ['parsed_rooms_selected', 'parsed_rooms'],
        ['parsed_house_location_selected', 'parsed_house_location'],
        ['features_inside_selected', 'features_inside'],
        ['features_nearby_selected', 'features_nearby'],
        ['plot_features_selected', 'plot_features'],
        ['house_conditions_selected', 'house_conditions'],
        ['water_selected', 'water'],
        ['heating_selected', 'heating'],
        ['communications_selected', 'communications'],
        ['realty_type_selected', 'realty_type']
      ]
      const skipKeys = new Set(arrayMappings.map(([key]) => key))

      Object.entries(filters).forEach(([key, value]) => {
        if (skipKeys.has(key)) return
        if (value === '' || value === null || value === undefined) return
        if (typeof value === 'boolean') {
          if (!value) return
          params.append(key, String(value))
          return
        }
        params.append(key, String(value))
      })

      arrayMappings.forEach(([key, param]) => {
        const values = filters[key]
        if (!Array.isArray(values)) return
        values.forEach(v => {
          if (v !== '' && v !== null && v !== undefined) {
            params.append(param, String(v))
          }
        })
      })

      const map = mapInstanceRef.current
      if (map && typeof map.getCenter === 'function') {
        const center = map.getCenter()
        if (Array.isArray(center) && center.length >= 2) {
          params.set('lat', Number(center[0]).toFixed(6))
          params.set('lon', Number(center[1]).toFixed(6))
        }
        const zoom = map.getZoom()
        if (typeof zoom === 'number') {
          params.set('zoom', String(zoom))
        }
      }

      const adId = activeAdIdRef.current || pendingOpenAdIdRef.current
      if (adId) {
        params.set('ad_id', String(adId))
      }
      if (debugTokenRef.current) {
        params.set('debug', debugTokenRef.current)
      }
      if (changeMode && changeTokenRef.current) {
        params.set('change', changeTokenRef.current)
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`
      try {
        window.history.replaceState({}, '', newUrl)
      } catch (e) {
        // Браузер затроттлил replaceState — не страшно, URL догонит
        // следующим вызовом; главное не уронить вызвавший обработчик.
      }
    }

    if (syncUrlRef.current) {
      syncUrlRef.current()
    }
  }, [filters, changeMode])

  useEffect(() => {
    window.__regeocodeAd = async (buttonEl) => {
      if (!debugModeRef.current || !buttonEl) return false
      const adId = String(buttonEl.getAttribute('data-ad-id') || '').trim()
      if (!adId) return false

      const statusEl = buttonEl.parentElement
        ? buttonEl.parentElement.querySelector('.balloon-debug-regeocode-status')
        : null
      const setStatus = (text, isError = false) => {
        if (!statusEl) return
        statusEl.textContent = text || ''
        statusEl.classList.toggle('error', Boolean(isError))
      }

      const originalText = String(buttonEl.textContent || t('debug_regeocode_button'))
      buttonEl.disabled = true
      setStatus(t('debug_regeocode_status_running'))

      try {
        const regeocodeHeaders = buildDebugHeaders()
        const resp = await fetch(
          `${API_BASE}/api/ads/${encodeURIComponent(adId)}/regeocode`,
          { method: 'POST', headers: regeocodeHeaders }
        )
        let payload = null
        try {
          payload = await resp.json()
        } catch (_) {
          payload = null
        }
        if (!resp.ok) {
          const detail = payload && payload.detail
            ? String(payload.detail)
            : `${t('debug_regeocode_status_error')} (${resp.status})`
          throw new Error(detail)
        }

        const changed = payload && Object.prototype.hasOwnProperty.call(payload, 'changed')
          ? Boolean(payload.changed)
          : true
        if (!changed) {
          const unchangedText = t('debug_regeocode_status_unchanged')
          setStatus(unchangedText)
          pushFlash(t('debug_regeocode_flash_unchanged'))
          return true
        }

        pendingOpenAdIdRef.current = adId
        activeAdIdRef.current = adId
        const fetchFn = fetchAndRenderRef.current
        if (typeof fetchFn === 'function') {
          await fetchFn()
        }
        setStatus(t('debug_regeocode_status_done'))
        pushFlash(t('debug_regeocode_status_done'))
        return true
      } catch (err) {
        const fallback = t('debug_regeocode_status_error')
        const message = err && err.message ? String(err.message) : fallback
        setStatus(message, true)
        pushFlash(message, true, 4200)
        return false
      } finally {
        buttonEl.disabled = false
        buttonEl.textContent = originalText
      }
    }

    return () => {
      delete window.__regeocodeAd
    }
  }, [t])

  useEffect(() => {
    // Кнопки рендерятся только при can_edit (change-режим или автор своего
    // объявления), поэтому ?change= здесь не требуется.
    window.__startChangeLocation = (buttonEl) => {
      if (!buttonEl) return false
      const adId = String(buttonEl.getAttribute('data-ad-id') || '').trim()
      if (!adId) return false

      let origLat = null
      let origLon = null
      const placemark = placemarkByAdIdRef.current.get(adId)
      if (placemark && placemark.geometry && typeof placemark.geometry.getCoordinates === 'function') {
        const c = placemark.geometry.getCoordinates()
        if (Array.isArray(c) && c.length >= 2) {
          origLat = Number(c[0])
          origLon = Number(c[1])
        }
      }

      changePickRef.current = { active: true, adId, origLat, origLon }
      const map = mapInstanceRef.current
      if (map && map.balloon && typeof map.balloon.close === 'function') {
        map.balloon.close()
      }
      setPendingChange(null)
      setPickingAdId(adId)
      return true
    }

    window.__toggleHideAd = (buttonEl) => {
      if (!buttonEl) return false
      const adId = String(buttonEl.getAttribute('data-ad-id') || '').trim()
      if (!adId) return false
      const currentlyHidden = String(buttonEl.getAttribute('data-hidden') || '') === '1'
      const map = mapInstanceRef.current
      if (map && map.balloon && typeof map.balloon.close === 'function') {
        map.balloon.close()
      }
      setPendingHide({ adId, makeHidden: !currentlyHidden })
      return true
    }

    return () => {
      delete window.__startChangeLocation
      delete window.__toggleHideAd
    }
  }, [changeMode])

  /* Balloon carousel: global handlers for prev/next (balloon HTML is injected by Yandex) */
  useEffect(() => {
    window.__balloonCarouselPrev = (carouselId) => {
      const root = document.getElementById(carouselId)
      if (!root) return
      const slides = root.querySelectorAll('.balloon-slide')
      if (!slides.length) return
      let idx = -1
      slides.forEach((s, i) => { if (s.classList.contains('active')) idx = i })
      if (idx < 0) idx = 0
      idx = idx - 1
      if (idx < 0) idx = slides.length - 1
      slides.forEach(s => s.classList.remove('active'))
      slides[idx].classList.add('active')
    }
    window.__balloonCarouselNext = (carouselId) => {
      const root = document.getElementById(carouselId)
      if (!root) return
      const slides = root.querySelectorAll('.balloon-slide')
      if (!slides.length) return
      let idx = -1
      slides.forEach((s, i) => { if (s.classList.contains('active')) idx = i })
      if (idx < 0) idx = 0
      idx = (idx + 1) % slides.length
      slides.forEach(s => s.classList.remove('active'))
      slides[idx].classList.add('active')
    }
    return () => {
      delete window.__balloonCarouselPrev
      delete window.__balloonCarouselNext
    }
  }, [])

  /* =========================
     RENDER HELPERS
  ========================= */
  const renderMinMax = (label, minKey, maxKey) => (
    <div className="filter-block">
      <div className="filter-label">{label}</div>
      <div style={{ display: 'flex', gap: '4%' }}>
        <input
          style={{ width: '48%' }}
          type="number"
          placeholder={t('placeholder_min')}
          value={filters[minKey]}
          onChange={e => setF(minKey, e.target.value)}
        />
        <input
          style={{ width: '48%' }}
          type="number"
          placeholder={t('placeholder_max')}
          value={filters[maxKey]}
          onChange={e => setF(maxKey, e.target.value)}
        />
      </div>
    </div>
  )

  const renderRooms = () => (
    <div className="filter-block">
      <div className="filter-label">{t('filters_rooms')}</div>
      <div className="rooms-filter">
        {ROOM_BUTTONS.map(r => {
          const isActive = r === '5+'
            ? String(filters.parsed_rooms_min) === '5'
            : filters.parsed_rooms_selected.includes(String(r))
          return (
          <button
            key={r}
            type="button"
            className={`room-btn ${isActive ? 'active' : ''}`}
            onClick={() => {
              if (r === '5+') {
                setFilters(prev => ({
                  ...prev,
                  parsed_rooms_min: String(prev.parsed_rooms_min) === '5' ? '' : '5'
                }))
              } else {
                const s = String(r)
                setFilters(prev => {
                  const arr = [...prev.parsed_rooms_selected]
                  const i = arr.indexOf(s)
                  i >= 0 ? arr.splice(i, 1) : arr.push(s)
                  return { ...prev, parsed_rooms_selected: arr }
                })
              }
            }}
          >
            {r}
          </button>
        )})}
      </div>
    </div>
  )

  const renderSelect = (label, value, onChange, options, defaultLabel) => (
    <div className="filter-block">
      <div className="filter-label">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}>
        {defaultLabel ? <option value="">{defaultLabel}</option> : null}
        {(options || []).map(x => (
          <option key={x} value={x}>{t.opt(x)}</option>
        ))}
      </select>
    </div>
  )

  const renderToggleGroup = (label, options, selected, onToggle) => (
    <div className="filter-block">
      <div className="filter-label">{label}</div>
      <div className="toggle-grid">
        {(options || []).map(x => {
          const active = selected.includes(x)
          return (
            <button
              key={x}
              type="button"
              className={'toggle-btn' + (active ? ' active' : '')}
              onClick={() => onToggle(x)}
            >
              {t.opt(x)}
            </button>
          )
        })}
      </div>
    </div>
  )

  const renderYesNoSelect = (label, key) => (
    <div className="filter-block">
      <div className="filter-label">{label}</div>
      <select value={filters[key]} onChange={e => setF(key, e.target.value)}>
        <option value="">{t('common_any_value')}</option>
        <option value="true">{t('common_yes')}</option>
        <option value="false">{t('common_no')}</option>
      </select>
    </div>
  )

  const FILTER_RENDERERS = {
    housing_type: () => renderSelect(
      t('filters_housing_type'),
      filters.parsed_housing_type,
      v => setF('parsed_housing_type', v),
      opts.housing_types,
      t('common_any_type')
    ),
    rooms: renderRooms,
    area_total: () => renderMinMax(t('filters_area_total'), 'total_area_min', 'total_area_max'),
    area_living: () => renderMinMax(t('filters_area_living'), 'living_area_min', 'living_area_max'),
    area_kitchen: () => renderMinMax(t('filters_area_kitchen'), 'kitchen_area_min', 'kitchen_area_max'),
    area_usable: () => renderMinMax(t('filters_area_usable'), 'parsed_usable_area_min', 'parsed_usable_area_max'),
    plot_area: () => renderMinMax(t('filters_plot_area'), 'parsed_plot_area_min', 'parsed_plot_area_max'),
    floor: () => renderMinMax(t('filters_floor'), 'floor_min', 'floor_max'),
    floors_total: () => renderMinMax(t('filters_floors_total'), 'floors_total_min', 'floors_total_max'),
    ceiling: () => renderMinMax(t('filters_ceiling'), 'parsed_ceiling_height_min', 'parsed_ceiling_height_max'),
    year_built: () => renderMinMax(t('filters_year_built'), 'parsed_year_built_min', 'parsed_year_built_max'),

    building_type: () => renderSelect(
      t('filters_building_type'),
      filters.parsed_building_type,
      v => setF('parsed_building_type', v),
      opts.building_types,
      t('common_any_type')
    ),

    layout: () => renderSelect(
      t('filters_layout'),
      filters.parsed_layout,
      v => setF('parsed_layout', v),
      opts.layouts,
      t('common_any_layout')
    ),

    bathroom: () => renderSelect(
      t('filters_bathroom'),
      filters.parsed_bathroom,
      v => setF('parsed_bathroom', v),
      opts.bathrooms,
      t('common_any_bathroom')
    ),

    furnished: () => renderYesNoSelect(t('filters_furnished'), 'parsed_furnished'),

    renovation: () => renderSelect(
      t('filters_renovation'),
      filters.parsed_renovation,
      v => setF('parsed_renovation', v),
      opts.renovations,
      t('common_any_renovation')
    ),

    gas: () => renderSelect(
      t('filters_gas'),
      filters.parsed_gas,
      v => setF('parsed_gas', v),
      opts.gases,
      t('common_gas_any')
    ),

    features_inside: () => {
      const label = objectTypeKey === 'Коммерческие помещения'
        ? t('filters_features_inside_commercial')
        : t('filters_features_inside')
      return renderToggleGroup(
        label,
        opts.features_inside,
        filters.features_inside_selected,
        x => {
          setFilters(prev => {
            const arr = [...prev.features_inside_selected]
            const idx = arr.indexOf(x)
            idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
            return { ...prev, features_inside_selected: arr }
          })
        }
      )
    },

    features_nearby: () => renderToggleGroup(
      t('filters_features_nearby'),
      opts.features_nearby,
      filters.features_nearby_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.features_nearby_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, features_nearby_selected: arr }
        })
      }
    ),

    plot_features: () => renderToggleGroup(
      t('filters_plot_features'),
      opts.plot_features,
      filters.plot_features_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.plot_features_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, plot_features_selected: arr }
        })
      }
    ),

    house_conditions: () => renderToggleGroup(
      t('filters_house_conditions'),
      opts.house_conditions,
      filters.house_conditions_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.house_conditions_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, house_conditions_selected: arr }
        })
      }
    ),

    water: () => renderToggleGroup(
      t('filters_water'),
      opts.water,
      filters.water_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.water_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, water_selected: arr }
        })
      }
    ),

    heating: () => {
      if (objectTypeKey === 'От застройщика') {
        const value = filters.heating_selected[0] || ''
        return renderSelect(
          t('filters_heating'),
          value,
          v => setF('heating_selected', v ? [v] : []),
          opts.heating,
          t('common_any_heating')
        )
      }
      return renderToggleGroup(
        t('filters_heating'),
        opts.heating,
        filters.heating_selected,
        x => {
          setFilters(prev => {
            const arr = [...prev.heating_selected]
            const idx = arr.indexOf(x)
            idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
            return { ...prev, heating_selected: arr }
          })
        }
      )
    },

    communications: () => renderToggleGroup(
      t('filters_communications'),
      opts.communications,
      filters.communications_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.communications_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, communications_selected: arr }
        })
      }
    ),

    realty_type: () => renderToggleGroup(
      t('filters_realty_type'),
      opts.realty_types,
      filters.realty_type_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.realty_type_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, realty_type_selected: arr }
        })
      }
    ),

    house_location: () => renderToggleGroup(
      t('filters_house_location'),
      opts.house_locations,
      filters.parsed_house_location_selected,
      x => {
        setFilters(prev => {
          const arr = [...prev.parsed_house_location_selected]
          const idx = arr.indexOf(x)
          idx >= 0 ? arr.splice(idx, 1) : arr.push(x)
          return { ...prev, parsed_house_location_selected: arr }
        })
      }
    ),

    land_purpose: () => renderSelect(
      t('filters_land_purpose'),
      filters.parsed_land_purpose,
      v => setF('parsed_land_purpose', v),
      opts.land_purposes,
      t('common_any_purpose')
    ),

    plot_type: () => renderSelect(
      t('filters_plot_type'),
      filters.parsed_plot_type,
      v => setF('parsed_plot_type', v),
      opts.plot_types,
      t('common_any_type_plot')
    ),

    garage_type: () => renderSelect(
      t('filters_garage_type'),
      filters.parsed_garage_type,
      v => setF('parsed_garage_type', v),
      opts.garage_types,
      t('common_any_type')
    ),

    garage_purpose: () => renderSelect(
      t('filters_garage_purpose'),
      filters.parsed_garage_purpose,
      v => setF('parsed_garage_purpose', v),
      opts.garage_purposes,
      t('common_any_purpose')
    ),

    commercial_location: () => renderSelect(
      t('filters_commercial_location'),
      filters.parsed_commercial_location,
      v => setF('parsed_commercial_location', v),
      opts.commercial_locations,
      t('common_any_location')
    ),

    commercial_has_plot: () => renderYesNoSelect(t('filters_commercial_has_plot'), 'parsed_commercial_has_plot'),

    electricity: () => renderYesNoSelect(t('filters_electricity'), 'parsed_electricity'),

    has_parking: () => renderYesNoSelect(t('filters_has_parking'), 'parsed_has_parking'),

    house_type: () => renderSelect(
      t('filters_house_type'),
      filters.parsed_house_type,
      v => setF('parsed_house_type', v),
      opts.house_types,
      t('common_any_type_house')
    ),

    housing_class: () => renderSelect(
      t('filters_housing_class'),
      filters.parsed_housing_class,
      v => setF('parsed_housing_class', v),
      opts.housing_classes,
      t('common_any_class')
    ),

    blocks_count: () => renderMinMax(t('filters_blocks_count'), 'parsed_blocks_count_min', 'parsed_blocks_count_max'),

    finishing: () => renderSelect(
      t('filters_finishing'),
      filters.parsed_finishing,
      v => setF('parsed_finishing', v),
      opts.finishings,
      t('common_any_finishing')
    ),

    facade: () => renderSelect(
      t('filters_facade'),
      filters.parsed_facade,
      v => setF('parsed_facade', v),
      opts.facades,
      t('common_any_facade')
    ),

    parking_type: () => renderSelect(
      t('filters_parking_type'),
      filters.parsed_parking_type,
      v => setF('parsed_parking_type', v),
      opts.parking_types,
      t('common_any_parking')
    ),

    construction_stage: () => renderSelect(
      t('filters_construction_stage'),
      filters.parsed_construction_stage,
      v => setF('parsed_construction_stage', v),
      opts.construction_stages,
      t('common_any_stage')
    ),

    parking_slots: () => (
      <div className="filter-block">
        <div className="filter-label">{t('filters_parking_slots_from')}</div>
        <input
          type="number"
          placeholder={t('placeholder_min')}
          value={filters.parsed_parking_slots_min}
          onChange={e => setF('parsed_parking_slots_min', e.target.value)}
        />
      </div>
    ),

    installment: () => (
      <div className="filter-block">
        <label className="filter-label">
          <input
            type="checkbox"
            checked={filters.parsed_installment}
            onChange={e => setF('parsed_installment', e.target.checked)}
          />{' '}
          {t('filters_installment')}
        </label>
      </div>
    ),

    mortgage: () => (
      <div className="filter-block">
        <label className="filter-label">
          <input
            type="checkbox"
            checked={filters.parsed_mortgage}
            onChange={e => setF('parsed_mortgage', e.target.checked)}
          />{' '}
          {t('filters_mortgage')}
        </label>
      </div>
    )
  }

  /* =========================
     RENDER
  ========================= */

  if (!opts) return <div>{t('loading')}</div>

  const visible = FILTERS_BY_OBJECT[objectTypeKey] || []
  const selectedCity = CITIES.find(c => c.id === selectedCityId) || CITIES[0]

  const getCityLabel = (city) => {
    const key = `city_${city.id}`
    const label = t(key)
    return label === key ? city.name : label
  }

  const filterFormContent = (
    <>
      <select value={filters.parsed_deal_type} onChange={e => setF('parsed_deal_type', e.target.value)}>
        {dealOptions.map(x => <option key={x} value={x}>{t.opt(x)}</option>)}
      </select>
      <select value={filters.parsed_object_type} onChange={e => setF('parsed_object_type', e.target.value)}>
        {objectOptions.map(x => <option key={x} value={x}>{t.opt(x)}</option>)}
      </select>
      {renderMinMax(t('filters_price'), 'price_min', 'price_max')}
      {visible.map(k => (
        <React.Fragment key={k}>
          {FILTER_RENDERERS[k]?.()}
        </React.Fragment>
      ))}
      <div className="filter-block">
        <div className="filter-label">
          {t('filters_commission_up_to')}: {filters.commission_max_percent}%
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={10}
          value={filters.commission_max_percent}
          onChange={e => setF('commission_max_percent', e.target.value)}
        />
      </div>
    </>
  )

  const closeFilters = () => setFiltersOverlayOpen(false)

  const handleCityChange = (e) => {
    const nextId = e.target.value
    setSelectedCityId(nextId)
    const city = CITIES.find(c => c.id === nextId)
    if (city && mapInstanceRef.current && typeof mapInstanceRef.current.setCenter === 'function') {
      mapInstanceRef.current.setCenter([city.lat, city.lon], city.zoom)
      if (typeof mapInstanceRef.current.setZoom === 'function') {
        mapInstanceRef.current.setZoom(city.zoom)
      }
    }
  }

  return (
    <div className="map-page">
      <header className="map-header">
        <div className="map-header-left">
          <div className="map-header-logo" aria-label="Logo">LOGO</div>
        </div>
        <div className="map-header-right">
          {adminUser ? (
            /* Тумблер debug-режима — только подтверждённому админу (GET /api/admin/me).
               Включает то же, что ?debug=<секрет> в адресе; положение живёт в
               localStorage до следующего переключения. Пока бэкенд не подтвердил
               режим (fc.debug), подпись приглушена. */
            <label
              className={
                'map-header-debug' +
                (debugEnabled && !debugConfirmed ? ' map-header-debug-pending' : '')
              }
              title={
                adminUser.login
                  ? `${t('header_debug_title')} (${adminUser.login})`
                  : t('header_debug_title')
              }
            >
              <input
                type="checkbox"
                className="map-header-debug-input"
                checked={debugEnabled}
                onChange={e => handleDebugToggle(e.target.checked)}
              />
              <span className="map-header-debug-track" aria-hidden="true" />
              <span className="map-header-debug-label">{t('header_debug')}</span>
            </label>
          ) : null}
          {(adminUser || debugConfirmed) ? (
            /* Служебная ссылка: дашборд телеграм-рассылки (tg-outreach) — админу
               (сессия подтверждена) и в debug-режиме по ?debug=. Сам дашборд
               просит логин/пароль админа, секрет в адрес не кладём. */
            <a
              className="map-header-outreach"
              href="/outreach"
              title={t('header_outreach_title')}
            >
              {t('header_outreach')}
            </a>
          ) : null}
          <div className="map-header-lang">
            <select
              className="map-header-lang-select"
              value={locale}
              onChange={e => {
                const code = e.target.value
                setStoredLocale(code)
                window.location.reload()
              }}
              title={t('header_language')}
              aria-label={t('header_language')}
            >
              {LOCALES.map(l => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
          </div>
          <div className="map-header-city">
            <select
              className="map-header-city-select"
              value={selectedCityId}
              onChange={handleCityChange}
              title={t('header_city_title')}
            >
              {CITIES.map(c => (
                <option key={c.id} value={c.id}>{getCityLabel(c)}</option>
              ))}
            </select>
            <span className="map-header-city-arrow" aria-hidden>▼</span>
          </div>
          {authUser ? (
            <div className="map-header-auth">
              <button
                type="button"
                className="map-header-avatar"
                onClick={() => setAuthMenuOpen(v => !v)}
                title={formatAuthPhone(authUser.phone)}
                aria-label={t('auth_menu_title')}
                aria-expanded={authMenuOpen}
                aria-haspopup="menu"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M12 12c2.65 0 4.8-2.15 4.8-4.8S14.65 2.4 12 2.4 7.2 4.55 7.2 7.2 9.35 12 12 12zm0 2.4c-3.2 0-9.6 1.61-9.6 4.8v2.4h19.2v-2.4c0-3.19-6.4-4.8-9.6-4.8z" />
                </svg>
              </button>
              {authMenuOpen ? (
                <div className="map-header-auth-menu" role="menu">
                  <div className="map-header-auth-phone">{formatAuthPhone(authUser.phone)}</div>
                  <button
                    type="button"
                    className="map-header-auth-logout"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    {t('auth_logout')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>
      <div className="map-container">
        <button
          type="button"
          className="filters-toggle"
          onClick={() => setFiltersOverlayOpen(true)}
          aria-label={t('header_filters')}
        >
          {t('header_filters')}
        </button>

        <div className="filters-panel">
          {filterFormContent}
        </div>

        {filtersOverlayOpen && (
          <div
            className="filters-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={t('header_filters')}
            onClick={e => e.target === e.currentTarget && closeFilters()}
          >
            <div className="filters-overlay-inner" onClick={e => e.stopPropagation()}>
              <div className="filters-overlay-header">
                <div className="filters-overlay-title">{t('header_filters')}</div>
                <button
                  type="button"
                  className="filters-close-btn"
                  aria-label={t('filters_close')}
                  onClick={closeFilters}
                >
                  ×
                </button>
              </div>
              <div className="filters-overlay-content filters-panel">
                {filterFormContent}
              </div>
              <div className="filters-overlay-footer">
                <button
                  type="button"
                  className="filters-apply-btn"
                  onClick={closeFilters}
                >
                  {t('header_done')}
                </button>
              </div>
            </div>
          </div>
        )}

        {flashMessage ? (
          <div
            className={`flash-toast${flashMessage.isError ? ' flash-toast-error' : ''}`}
            role="status"
            aria-live="polite"
          >
            {flashMessage.text}
          </div>
        ) : null}

        {pickingAdId && !pendingChange ? (
          <div className="change-banner" role="status" aria-live="polite">
            <span className="change-banner-text">{t('change_pick_hint')}</span>
            <button
              type="button"
              className="change-banner-cancel"
              onClick={exitPickMode}
            >
              {t('change_cancel')}
            </button>
          </div>
        ) : null}

        {pendingChange ? (
          <div
            className="change-dialog-overlay"
            onClick={e => { if (e.target === e.currentTarget) backToPicking() }}
          >
            <div className="change-dialog">
              <div className="change-dialog-title">{t('change_confirm_title')}</div>
              <div className="change-dialog-coords">
                {Number(pendingChange.lat).toFixed(6)}, {Number(pendingChange.lon).toFixed(6)}
              </div>
              <div className="change-dialog-hint">{t('change_confirm_hint')}</div>
              <div className="change-dialog-actions">
                <button
                  type="button"
                  className="change-dialog-btn change-dialog-cancel"
                  onClick={backToPicking}
                  disabled={changeSubmitting}
                >
                  {t('change_cancel')}
                </button>
                <button
                  type="button"
                  className="change-dialog-btn change-dialog-confirm"
                  onClick={submitChangeLocation}
                  disabled={changeSubmitting}
                >
                  {changeSubmitting ? t('change_saving') : t('change_confirm')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {pendingHide ? (
          <div
            className="change-dialog-overlay"
            onClick={e => { if (e.target === e.currentTarget && !hideSubmitting) setPendingHide(null) }}
          >
            <div className="change-dialog">
              <div className="change-dialog-title">
                {pendingHide.makeHidden ? t('change_hide_confirm_title') : t('change_show_confirm_title')}
              </div>
              <div className="change-dialog-hint">
                {pendingHide.makeHidden ? t('change_hide_confirm_hint') : t('change_show_confirm_hint')}
              </div>
              <div className="change-dialog-actions">
                <button
                  type="button"
                  className="change-dialog-btn change-dialog-cancel"
                  onClick={() => setPendingHide(null)}
                  disabled={hideSubmitting}
                >
                  {t('change_cancel')}
                </button>
                <button
                  type="button"
                  className="change-dialog-btn change-dialog-confirm"
                  onClick={submitHide}
                  disabled={hideSubmitting}
                >
                  {hideSubmitting ? t('change_saving') : t('change_confirm')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

      {/* КАРТА */}
      <div
        id="map"
        className={`map-canvas${pickingAdId ? ' map-canvas-picking' : ''}`}
        style={{ minHeight: '400px' }}
      />
      </div>
    </div>
  )
}
